import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Box,
    CircularProgress,
    Switch,
    FormControlLabel,
    Divider,
    IconButton,
    InputAdornment,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { path } from "../../../routes";

const EditUser_modal = (props) => {
    const [username, setUsername] = useState(props.user?.username || "");
    const [password, setPassword] = useState("");
    const [selectedOrg, setSelectedOrg] = useState(
        props.user?.cOrganization_id || props.orgId || ""
    );
    const [orgList, setOrgList] = useState([]);
    const [loading, setLoading] = useState(false);
    const [permLoading, setPermLoading] = useState(false);
    const [allowKioskSettings, setAllowKioskSettings] = useState(false); // Defaultně OFF
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        if (props.user) {
            setUsername(props.user.username || "");
            setSelectedOrg(props.user.cOrganization_id || props.orgId || "");
        }
    }, [props.user, props.orgId]);

    useEffect(() => {
        getOrganizations();
    }, []);

    useEffect(() => {
        const userId = props.user?.id;
        if (!userId) return;
        let cancelled = false;
        const loadPerm = async () => {
            setPermLoading(true);
            try {
                const resp = await axios.get(`${path}/getUserKioskSettingsPermission/${userId}`);
                if (cancelled) return;
                // Pokud není v DB nebo je null/undefined → false (OFF)
                const value = resp.data?.allowKioskSettings;
                setAllowKioskSettings(value === true || value === 1);
            } catch (e) {
                // Pokud není v DB (chyba) → defaultně false (OFF)
                if (!cancelled) setAllowKioskSettings(false);
            } finally {
                if (!cancelled) setPermLoading(false);
            }
        };
        loadPerm();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.user?.id]);

    const getOrganizations = async () => {
        try {
            let response = await axios.get(`${path}/getOrganizations`);
            setOrgList(response.data);
        } catch (error) {
            console.log(error);
        }
    };

    const handleTextChange = (event) => {
        setUsername(event.target.value);
    };

    const handlePasswordChange = (event) => {
        setPassword(event.target.value);
    };

    const handleOrgChange = (event) => {
        setSelectedOrg(event.target.value);
    };

    const close_handler = () => {
        props.close();
    };

    const handleSubmit = async () => {
        if (!username || !selectedOrg) {
            alert("Please fill in all required fields");
            return;
        }

        setLoading(true);
        try {
            const updateData = {
                username: username,
                organization: selectedOrg,
            };

            if (password) {
                updateData.password = password;
            }

            await axios.put(`${path}/updateUser/${props.user.id}`, updateData);

            // Save permission: can user edit kiosk settings?
            await axios.put(`${path}/setUserKioskSettingsPermission/${props.user.id}`, {
                allowKioskSettings: Boolean(allowKioskSettings)
            });

            alert("User updated successfully");
            props.close();
        } catch (error) {
            const status = error?.response?.status;
            const data = error?.response?.data;
            if (status === 409 && data?.missingTable) {
                alert(
                    `Cannot save permission because database table is missing: ${data.missingTable}\n\n` +
                    `${data.hint || 'Run the migration SQL on the production database.'}`
                );
            } else {
                alert("Error updating user");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog
            open={true}
            onClose={close_handler}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: { xs: 0, sm: "12px" },
                    maxHeight: { xs: "100vh", sm: "90vh" },
                    margin: { xs: 0, sm: "32px" },
                    width: { xs: "100%", sm: "auto" },
                },
            }}
        >
            <DialogTitle
                sx={{
                    fontSize: { xs: "18px", sm: "20px" },
                    fontWeight: 600,
                    pb: 1.5,
                    borderBottom: "1px solid #e0e0e0",
                }}
            >
                Edit User
            </DialogTitle>

            <DialogContent
                sx={{
                    pt: 3,
                    pb: 3,
                    px: { xs: 2, sm: 3 },
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                        paddingTop: 2,
                    }}
                >
                    <TextField
                        sx={{ width: "100%" }}
                        id="username"
                        label="Username"
                        variant="outlined"
                        value={username}
                        onChange={handleTextChange}
                        fullWidth
                    />
                    <TextField
                        sx={{ width: "100%" }}
                        id="password"
                        label="New password (leave empty to keep current)"
                        type={showPassword ? "text" : "password"}
                        variant="outlined"
                        value={password}
                        onChange={handlePasswordChange}
                        fullWidth
                        helperText="Leave empty to keep current password"
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                        edge="end"
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                    />
                    <FormControl sx={{ width: "100%" }}>
                        <InputLabel id="org-select-label">Organization</InputLabel>
                        <Select
                            labelId="org-select-label"
                            value={selectedOrg}
                            label="Organization"
                            onChange={handleOrgChange}
                        >
                            {orgList.map((item) => (
                                <MenuItem key={item.id} value={item.id}>
                                    {item.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider sx={{ my: 1 }} />

                    <FormControlLabel
                        control={
                            <Switch
                                checked={allowKioskSettings}
                                onChange={(e) => setAllowKioskSettings(e.target.checked)}
                                disabled={permLoading}
                            />
                        }
                        label={permLoading ? "Loading settings permission…" : "User can edit kiosk settings"}
                    />
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    pb: { xs: 2, sm: 2.5 },
                    pt: 2,
                    borderTop: "1px solid #e0e0e0",
                    gap: { xs: 1, sm: 1.5 },
                    flexDirection: { xs: "column", sm: "row" },
                }}
            >
                <Button
                    onClick={close_handler}
                    disabled={loading}
                    variant="outlined"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        order: { xs: 2, sm: 1 },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    disabled={!username.trim() || !selectedOrg || loading}
                    onClick={handleSubmit}
                    variant="contained"
                    sx={{
                        width: { xs: "100%", sm: "auto" },
                        minWidth: { xs: "100%", sm: 120 },
                        height: { xs: "48px", sm: "auto" },
                        fontWeight: 600,
                        order: { xs: 1, sm: 2 },
                        boxShadow: "0 4px 12px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                            boxShadow: "0 6px 16px rgba(25, 118, 210, 0.4)",
                        },
                        "&:disabled": {
                            backgroundColor: "#e0e0e0",
                            color: "#9e9e9e",
                        },
                    }}
                >
                    {loading ? (
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <CircularProgress size={20} color="inherit" />
                            <span>Saving...</span>
                        </Box>
                    ) : (
                        "Save"
                    )}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditUser_modal;
